import AsyncStorage from '@react-native-async-storage/async-storage';
import InAppReview from 'react-native-in-app-review';
import { Linking, Platform } from 'react-native';
import { getVersion, getBundleId } from 'react-native-device-info';
import { appAnalytics } from './appAnalytics';
import { buildAppAnalyticsContext } from './appAnalyticsContext';
import type { ReviewPromptSource, ReviewSource } from './appAnalyticsTypes';

// Persistent keys
const REVIEW_STATE_KEY = '@app_review_state_v2';

// Qualifying actions
export type ReviewActionType =
  | 'SELL_COMPLETED'
  | 'ORDER_SAVED'
  | 'REPORT_DOWNLOADED'
  | 'PO_SENT'
  | 'EMPLOYEE_PASSWORD_RESET'
  | 'EMPLOYEE_REACTIVATED'
  | 'PRODUCT_TRANSFERRED'
  | 'MPESA_CONNECTED'
  | 'PRODUCT_UPDATED';

const ACTION_TO_SOURCE: Record<ReviewActionType, ReviewPromptSource> = {
  SELL_COMPLETED: 'sell_completed',
  ORDER_SAVED: 'order_saved',
  REPORT_DOWNLOADED: 'report_downloaded',
  PO_SENT: 'po_sent',
  EMPLOYEE_PASSWORD_RESET: 'employee_password_reset',
  EMPLOYEE_REACTIVATED: 'employee_reactivated',
  PRODUCT_TRANSFERRED: 'product_transferred',
  MPESA_CONNECTED: 'mpesa_connected',
  PRODUCT_UPDATED: 'product_updated',
};

type ReviewState = {
  actionCount: number; // qualifying actions counter
  lastPromptedAtActionCount: number | null;
  promptCycleIndex: number; // 0:first, 1:+20, 2:+55, 3:+95, then cap
  lastPromptedAt: string | null; // ISO date
  promptedForVersion: string | null; // app version gate
};

const REVIEW_PROMPT_TITLE = 'Loving the App?';

const getStoreLabel = () =>
  Platform.OS === 'ios' ? 'Apple App Store' : 'Google Play Store';

const getReviewPromptMessage = () =>
  `Your support means everything! Could you spare a moment to give us a quick ${getStoreLabel()} review?\n\nYour positive review helps other people find the app. 💙`;

const storeChannel = (): 'play' | 'app_store' =>
  Platform.OS === 'ios' ? 'app_store' : 'play';

class ReviewService {
  private static _inFlight = false;
  private static iosAppStoreId: string | undefined;
  private static androidPackageName: string | undefined;
  // Optional UI hook for showing a custom fallback rating prompt (star modal)
  // Should resolve to true when user accepts to proceed to the store, false when cancelled
  private static fallbackPromptPresenter?: (params: {
    title?: string;
    message?: string;
    defaultRating?: number;
    hideStars?: boolean;
    reason: 'not_available' | 'dismissed' | 'error';
    source: ReviewSource;
  }) => Promise<
    boolean | { accepted: boolean; rating?: number; openedStore?: boolean }
  >;

  static setIOSAppStoreId(id: string) {
    this.iosAppStoreId = id;
  }

  static setAndroidPackageName(packageName: string) {
    this.androidPackageName = packageName;
  }

  // Register a UI presenter for a fallback rating modal (implemented in app layer)
  static registerFallbackPromptPresenter(
    presenter: (params: {
      title?: string;
      message?: string;
      defaultRating?: number;
      hideStars?: boolean;
      reason: 'not_available' | 'dismissed' | 'error';
      source: ReviewSource;
    }) => Promise<
      boolean | { accepted: boolean; rating?: number; openedStore?: boolean }
    >,
  ) {
    this.fallbackPromptPresenter = presenter;
  }

  static sourceForAction(action: ReviewActionType): ReviewPromptSource {
    return ACTION_TO_SOURCE[action];
  }

  // Public API to call from success moments
  static async onEligibleAction(action: ReviewActionType): Promise<void> {
    if (!(action in ACTION_TO_SOURCE)) return;

    if (this._inFlight) return;
    this._inFlight = true;
    try {
      // Respect platform capability — web/electron remain no-op for auto prompts
      if (!InAppReview.isAvailable()) return;

      const version = getVersion();
      let state = await this.getReviewState();
      state = this.resetIfNewVersion(state, version);

      // Only count whitelisted actions
      state.actionCount = (state.actionCount || 0) + 1;
      await this.saveReviewState(state);

      // Enforce cap: max 4 prompts per version (cycles 0..3)
      if (state.promptCycleIndex >= 4) return;

      // Check threshold for this cycle
      const requiredDelta = this.requiredDeltaForCycle(state.promptCycleIndex);
      const lastAt = state.lastPromptedAtActionCount ?? 0;
      const sinceLast = state.actionCount - lastAt;

      // First eligible action after install/update: cycle 0 always attempts
      const shouldAttempt =
        state.promptCycleIndex === 0 ? true : sinceLast >= requiredDelta;

      if (!shouldAttempt) return;

      const source = this.sourceForAction(action);

      // Cycle 0: native in-app review (quick star rating)
      // Cycle 1+: store modal → store listing (where text reviews happen)
      const useStoreModal =
        state.promptCycleIndex > 0 &&
        this.fallbackPromptPresenter != null;

      try {
        if (useStoreModal) {
          appAnalytics.track('review_store_modal_requested', {
            ...buildAppAnalyticsContext(),
            source,
            prompt_cycle: state.promptCycleIndex,
            action_count: state.actionCount,
          });
          const res = await this.fallbackPromptPresenter!({
            title: REVIEW_PROMPT_TITLE,
            message: getReviewPromptMessage(),
            defaultRating: 5,
            hideStars: true,
            reason: 'not_available' as const,
            source,
          });
          const accepted =
            typeof res === 'boolean' ? res : !!res?.accepted;
          const openedStore =
            typeof res === 'object' ? !!res?.openedStore : false;
          if (accepted && !openedStore) {
            await this.openStoreListing();
          }
        } else {
          appAnalytics.track('review_native_requested', {
            ...buildAppAnalyticsContext(),
            source,
            prompt_cycle: state.promptCycleIndex,
            action_count: state.actionCount,
          });
          await InAppReview.RequestInAppReview();
        }
      } catch (err) {
        // Swallow; do not block cashier flow
      } finally {
        // Update prompt bookkeeping regardless of UI display result
        state.lastPromptedAt = new Date().toISOString();
        state.lastPromptedAtActionCount = state.actionCount;
        state.promptCycleIndex = state.promptCycleIndex + 1;
        await this.saveReviewState(state);
      }
    } finally {
      this._inFlight = false;
    }
  }

  // Explicit CTA: call review now; if not shown/dismissed, caller can route to feedback
  static async requestFromCTA(onOpenFeedback?: () => void): Promise<void> {
    // Try native in-app review first; if not shown/limited, fall back to store listing.
    console.log('requestFromCTA', InAppReview.isAvailable());
    if (!InAppReview.isAvailable()) {
      const accepted = await this.confirmStoreRedirect({
        reason: 'not_available',
        source: 'sidebar',
      });
      if (accepted) {
        const opened = await this.openStoreListing();
        if (!opened && onOpenFeedback) onOpenFeedback();
      } else if (onOpenFeedback) {
        onOpenFeedback();
      }
      return;
    }
    const version = getVersion();
    let state = await this.getReviewState();
    state = this.resetIfNewVersion(state, version);
    console.log('state', state);
    try {
      appAnalytics.track('review_native_requested', {
        ...buildAppAnalyticsContext(),
        source: 'sidebar',
        prompt_cycle: state.promptCycleIndex,
        action_count: state.actionCount,
      });
      const finished = await InAppReview.RequestInAppReview();
      console.log('finished', finished);
      if (!finished) {
        const accepted = await this.confirmStoreRedirect({
          reason: 'dismissed',
          source: 'sidebar',
        });
        if (accepted) {
          const opened = await this.openStoreListing();
          console.log('opened', opened);
          if (!opened && onOpenFeedback) onOpenFeedback();
        } else if (onOpenFeedback) {
          onOpenFeedback();
        }
      }
    } catch (e) {
      console.log('Error requesting review:', e);
      const accepted = await this.confirmStoreRedirect({
        reason: 'error',
        source: 'sidebar',
      });
      if (accepted) {
        const opened = await this.openStoreListing();
        if (!opened && onOpenFeedback) onOpenFeedback();
      } else if (onOpenFeedback) {
        onOpenFeedback();
      }
    } finally {
      state.lastPromptedAt = new Date().toISOString();
      state.lastPromptedAtActionCount = state.actionCount;
      state.promptCycleIndex = Math.min(state.promptCycleIndex + 1, 4);
      console.log('state', state);
      await this.saveReviewState(state);
    }
  }

  // Show custom modal first; on accept try native in-app review, otherwise open store
  static async requestWithModalFirst(
    onOpenFeedback?: () => void,
  ): Promise<void> {
    try {
      // Ensure we can present a modal; if not registered, fallback to existing CTA flow
      if (!this.fallbackPromptPresenter) {
        await this.requestFromCTA(onOpenFeedback);
        return;
      }

      const version = getVersion();
      let state = await this.getReviewState();
      state = this.resetIfNewVersion(state, version);

      // Always show our custom modal first
      const res = await this.fallbackPromptPresenter({
        title: REVIEW_PROMPT_TITLE,
        message: getReviewPromptMessage(),
        defaultRating: 5,
        hideStars: true,
        // Reason is just for copy/context; reusing an existing union value
        reason: 'not_available',
        source: 'sidebar',
      });

      const accepted = typeof res === 'boolean' ? res : !!res?.accepted;

      if (accepted) {
        // Try native in-app review first
        if (InAppReview.isAvailable()) {
          try {
            appAnalytics.track('review_native_requested', {
              ...buildAppAnalyticsContext(),
              source: 'sidebar',
              prompt_cycle: state.promptCycleIndex,
              action_count: state.actionCount,
            });
            const finished = await InAppReview.RequestInAppReview();
            if (!finished) {
              // If prompt not shown or user dismissed, open store page
              const opened = await this.openStoreListing();
              if (!opened && onOpenFeedback) onOpenFeedback();
            }
          } catch (e) {
            const opened = await this.openStoreListing();
            if (!opened && onOpenFeedback) onOpenFeedback();
          }
        } else {
          const opened = await this.openStoreListing();
          if (!opened && onOpenFeedback) onOpenFeedback();
        }
      } else {
        // User declined CTA -> open store page to maximize conversions
        const opened = await this.openStoreListing();
        if (!opened && onOpenFeedback) onOpenFeedback();
      }

      // Bookkeeping similar to CTA path
      state.lastPromptedAt = new Date().toISOString();
      state.lastPromptedAtActionCount = state.actionCount;
      state.promptCycleIndex = Math.min(state.promptCycleIndex + 1, 4);
      await this.saveReviewState(state);
    } catch (e) {
      // As a last resort, try legacy CTA path to avoid silent failures
      try {
        await this.requestFromCTA(onOpenFeedback);
      } catch {}
    }
  }

  // ----- internals -----
  static async getReviewState(): Promise<ReviewState> {
    try {
      const savedState = await AsyncStorage.getItem(REVIEW_STATE_KEY);
      if (savedState) {
        const parsed = JSON.parse(savedState);
        return {
          actionCount: Number(parsed.actionCount || 0),
          lastPromptedAtActionCount: parsed.lastPromptedAtActionCount ?? null,
          promptCycleIndex: Number(parsed.promptCycleIndex || 0),
          lastPromptedAt: parsed.lastPromptedAt ?? null,
          promptedForVersion: parsed.promptedForVersion ?? null,
        } as ReviewState;
      }
    } catch (error) {
      console.log('Error getting review state:', error);
    }
    return {
      actionCount: 0,
      lastPromptedAtActionCount: null,
      promptCycleIndex: 0,
      lastPromptedAt: null,
      promptedForVersion: null,
    };
  }

  static async saveReviewState(state: ReviewState): Promise<void> {
    try {
      await AsyncStorage.setItem(REVIEW_STATE_KEY, JSON.stringify(state));
    } catch (error) {
      console.log('Error saving review state:', error);
    }
  }

  private static resetIfNewVersion(
    state: ReviewState,
    version: string,
  ): ReviewState {
    if (state.promptedForVersion !== version) {
      return {
        ...state,
        actionCount: 0,
        lastPromptedAtActionCount: null,
        promptCycleIndex: 0,
        lastPromptedAt: null,
        promptedForVersion: version,
      };
    }
    return state;
  }

  private static requiredDeltaForCycle(cycleIndex: number): number {
    if (cycleIndex <= 0) return 0; // first eligible action
    if (cycleIndex === 1) return 20;
    if (cycleIndex === 2) return 55;
    return 95; // cycle 3 (then capped)
  }

  // Ask user via registered UI before redirecting to the store
  private static async confirmStoreRedirect(params: {
    reason: 'not_available' | 'dismissed' | 'error';
    source: ReviewSource;
  }): Promise<boolean> {
    try {
      if (!this.fallbackPromptPresenter) return false;
      const res = await this.fallbackPromptPresenter({
        title: REVIEW_PROMPT_TITLE,
        message: getReviewPromptMessage(),
        defaultRating: 5,
        hideStars: true,
        reason: params.reason,
        source: params.source,
      });

      if (typeof res === 'boolean') return res;
      return !!res?.accepted;
    } catch {
      return false;
    }
  }

  static getGooglePlayListingUrl(): string | null {
    try {
      const raw =
        (() => {
          try {
            return getBundleId();
          } catch {
            return undefined;
          }
        })() || this.androidPackageName;

      if (!raw) return null;

      const accurateBundleId = raw.replace(/(\.dev|\.staging|\.prod)$/, '');
      return `https://play.google.com/store/apps/details?id=${accurateBundleId}&showAllReviews=true`;
    } catch {
      return null;
    }
  }

  private static async openStoreListing(): Promise<boolean> {
    try {
      if (Platform.OS === 'web') {
        const url = this.getGooglePlayListingUrl();
        if (!url) return false;
        // Prefer navigating current tab on web (popup blockers may block new tabs).
        // For "open in new tab" on web we do it synchronously in the modal button handler.
        const win: any = (globalThis as any)?.window;
        if (win?.location?.assign) {
          win.location.assign(url);
          return true;
        }
        await Linking.openURL(url);
        return true;
      }
      if (Platform.OS === 'android') {
        const rawBundleId = (() => {
          try {
            return getBundleId();
          } catch {
            return this.androidPackageName;
          }
        })();
        if (!rawBundleId) return false;

        // Remove all known suffixes from bundleId for accurate store lookup
        const accurateBundleId = rawBundleId.replace(
          /(\.dev|\.staging|\.prod)$/,
          '',
        );
        const marketUrl = `market://details?id=${accurateBundleId}`;
        console.log('marketUrl', marketUrl);
        const webUrl = `https://play.google.com/store/apps/details?id=${accurateBundleId}&showAllReviews=true`;
        try {
          await Linking.openURL(marketUrl);
          return true;
        } catch {}
        await Linking.openURL(webUrl);
        return true;
      }
      if (Platform.OS === 'ios' && this.iosAppStoreId) {
        const url = `itms-apps://itunes.apple.com/app/id${this.iosAppStoreId}?action=write-review`;
        await Linking.openURL(url);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  // Public helper for CTA paths when you want to force opening the store page
  static async openStorePage(): Promise<boolean> {
    return this.openStoreListing();
  }

  static getStoreChannel(): 'play' | 'app_store' {
    return storeChannel();
  }

  // Public method to show modal first, then open store directly if accepted
  static async requestStoreWithModal(
    source: ReviewSource = 'sidebar',
  ): Promise<void> {
    if (!this.fallbackPromptPresenter) return;

    const res = await this.fallbackPromptPresenter({
      title: REVIEW_PROMPT_TITLE,
      message: getReviewPromptMessage(),
      defaultRating: 5,
      hideStars: true,
      reason: 'not_available',
      source,
    });

    const accepted = typeof res === 'boolean' ? res : !!res?.accepted;
    const openedStore = typeof res === 'object' ? !!res?.openedStore : false;

    if (accepted && !openedStore) {
      await this.openStoreListing();
    }
  }
}

export default ReviewService;
